import React, { useEffect, useState } from "react";
import { User, Phone, Mail, X, CreditCard } from "lucide-react";
import { createPortal } from "react-dom";
import { CiLocationOn } from "react-icons/ci";
import { LiaCitySolid } from "react-icons/lia";
import { GrGallery } from "react-icons/gr";
import axios from "axios";
import UploadImg from "../Cloudinary/UploadImg";

const AddFranchiseModal = ({ data, onClose, onSubmit }) => {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const [formData, setFormData] = useState({
    fullName: "",
    franchiseName: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    aadharCard: "",
    companyRegistrationProof: null,
  });

  useEffect(() => {
    if (data) {
      const newFormData = {
        fullName: data.fullName ?? "",
        franchiseName: data.franchiseName ?? data.name ?? "",
        email: data.email ?? "",
        phone: data.phone ?? "",
        city: data.city ?? "",
        address: data.address ?? data.location ?? "",
        aadharCard: data.aadharCard ?? "",
        companyRegistrationProof: data.companyRegistrationProof ?? null,
      };
      setFormData(newFormData);
    }
  }, [data]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "aadharCard") {
      let v = value.replace(/\D/g, "");
      if (v.length > 12) v = v.slice(0, 12);
      v = v.replace(/(\d{4})(?=\d)/g, "$1 ");
      setFormData((prev) => ({ ...prev, aadharCard: v }));
    } else if (files) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      let companyProofUrl = "";

      // Upload new proof if user selected a file
      if (formData.companyRegistrationProof instanceof File) {
        companyProofUrl = await UploadImg(formData.companyRegistrationProof);
      } else {
        companyProofUrl = formData.companyRegistrationProof || "";
      }

      const payload = {
        fullName: formData.fullName,
        franchiseName: formData.franchiseName,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        aadharCard: formData.aadharCard,
        companyRegistrationProof: companyProofUrl,
      };

      let response;
      if (data && data._id) {
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/franchise/edit-franchise/${data._id}`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );
        setSuccess("Franchise updated successfully!");
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/franchise/apply-franchise`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );
        setSuccess("Franchise added successfully!");
      }

      if (onSubmit) onSubmit(response.data);

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      if (err.response) {
        setError(
          err.response.data?.message ||
            `Error ${err.response.status}: ${err.response.statusText}`
        );
      } else if (err.request) {
        setError("No response from server. Check your connection.");
      } else {
        setError("Unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="max-w-3xl w-full mx-auto bg-white shadow-xl rounded-xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="py-1 bg-gradient-to-r from-[#0070a8] to-[#023955] shadow-lg relative">
            <p className="text-center text-xl font-normal p-2 text-white">
              {data && data._id ? "Edit Franchise" : "Add Franchise"}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
          </div>

          {success && (
            <div className="p-3 bg-green-100 text-green-700 text-sm text-center">
              {success}
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-100 text-red-700 text-sm text-center">
              {error}
            </div>
          )}

          <div className="p-6 md:p-8">
            <div className="max-w-2xl mx-auto space-y-5">
              {/* Full Name */}
              <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                <User size={18} className="text-gray-400" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter Full Name"
                  required
                  className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                />
              </div>

              {/* Franchise Name */}
              <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                <User size={18} className="text-gray-400" />
                <input
                  type="text"
                  name="franchiseName"
                  value={formData.franchiseName}
                  onChange={handleChange}
                  placeholder="Enter Franchise Name"
                  required
                  className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                />
              </div>

              {/* Phone & Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <Phone size={18} className="text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Mobile Number"
                    required
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                  />
                </div>

                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <Mail size={18} className="text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    required
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                  />
                </div>
              </div>

              {/* Address & City */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <CiLocationOn size={18} className="text-gray-400" />
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter Address"
                    required
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                  />
                </div>

                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <LiaCitySolid size={18} className="text-gray-400" />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter City"
                    required
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                  />
                </div>
              </div>

              {/* Aadhaar */}
              <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                <CreditCard size={18} className="text-gray-400" />
                <input
                  type="text"
                  name="aadharCard"
                  value={formData.aadharCard}
                  onChange={handleChange}
                  placeholder="1234 5678 9012"
                  className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                />
              </div>

              {/* Company Registration Proof */}
              <div className="space-y-3">
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <GrGallery size={18} className="text-gray-400" />
                  <input
                    type="file"
                    name="companyRegistrationProof"
                    accept="image/*"
                    onChange={handleChange}
                    required={!data || !data._id}
                    className="bg-transparent outline-none w-full text-gray-700 text-sm md:text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                {/* Preview existing image if available */}
                {formData.companyRegistrationProof &&
                  !(formData.companyRegistrationProof instanceof File) && (
                    <div className="flex justify-center">
                      <img
                        src={formData.companyRegistrationProof}
                        alt="Uploaded Proof"
                        className="max-h-40 rounded-lg shadow-md border"
                      />
                    </div>
                  )}
              </div>

              {/* Buttons */}
              <div className="flex gap-4 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gradient-to-r from-[#333333] to-[#cccccc] hover:from-[#cccccc] hover:to-[#333333] text-white font-medium py-2 rounded-lg text-sm shadow-md transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white font-medium py-2 rounded-lg text-sm shadow-md transition-all duration-300 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddFranchiseModal;
